import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Divider,
    Alert,
    CircularProgress
} from "@mui/material";
import { useNavigate } from 'react-router-dom';
import axiosInstance from "../AxiosInstance";
import UserBlockManagement from './UserBlockManagement';

const calendarRuleSets = [
    { value: 0, label: 'Calendar month (majority days)' },
    { value: 1, label: '5-4-4 retail calendar' }
];

const AdminPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [organisations, setOrganisations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [createNewOrg, setCreateNewOrg] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        orgName: '',
        startMonth: 1,
        startDayOfWeek: 1,
        fiscalCalendarRuleSet: 0
    });
    const [formErrors, setFormErrors] = useState({});

    const months = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
    ];

    const daysOfWeek = [
        { value: 1, label: 'Monday' },
        { value: 2, label: 'Tuesday' },
        { value: 3, label: 'Wednesday' },
        { value: 4, label: 'Thursday' },
        { value: 5, label: 'Friday' },
        { value: 6, label: 'Saturday' },
        { value: 7, label: 'Sunday' }
    ];

    useEffect(() => {
        fetchOrganisations();
    }, []);

    const fetchOrganisations = async () => {
        try {
            const response = await axiosInstance.get('/organisation');
            setOrganisations(response.data);
        } catch (error) {
            setError('Failed to fetch organisations');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormErrors(prev => ({ ...prev, [name]: undefined }));
    };

    const handleOrgChange = (e) => {
        setSelectedOrg(e.target.value);
        setCreateNewOrg(e.target.value === 'new');
        setFormErrors(prev => ({ ...prev, selectedOrg: undefined }));
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.email || formData.email.trim() === '') {
            errors.email = 'Email is required.';
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email.trim())) {
                errors.email = 'Please enter a valid email address.';
            }
        }
        if (!formData.password || formData.password.trim() === '') {
            errors.password = 'Password is required.';
        }
        if (!selectedOrg && !createNewOrg) {
            errors.selectedOrg = 'Please select an organisation.';
        }
        if (createNewOrg) {
            if (!formData.orgName || formData.orgName.trim() === '') {
                errors.orgName = 'Organisation name is required.';
            }
            if (!formData.startMonth) {
                errors.startMonth = 'Start month is required.';
            }
            if (!formData.startDayOfWeek) {
                errors.startDayOfWeek = 'Start day of week is required.';
            }
            if (formData.fiscalCalendarRuleSet === '' || formData.fiscalCalendarRuleSet === undefined || formData.fiscalCalendarRuleSet === null) {
                errors.fiscalCalendarRuleSet = 'Fiscal calendar is required.';
            }
        }
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        const errors = validateForm();
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            return;
        }
        setLoading(true);
        try {
            // First register the user
            const registerResponse = await axiosInstance.post('/auth/register', {
                email: formData.email,
                password: formData.password
            });
            const userData = registerResponse.data;
            // Then add user to organisation
            const addToOrgPayload = createNewOrg
                ? {
                    userId: userData.userId,
                    organisationName: formData.orgName,
                    startMonth: formData.startMonth,
                    startDayOfWeek: formData.startDayOfWeek,
                    fiscalCalendarRuleSet: formData.fiscalCalendarRuleSet
                }
                : {
                    userId: userData.userId,
                    organisationId: selectedOrg
                };
            await axiosInstance.post('/organisation/add-to-organisation', addToOrgPayload);
            setSuccess('User registered and added to organisation successfully');
            setFormData({
                email: '',
                password: '',
                orgName: '',
                startMonth: 1,
                startDayOfWeek: 1,
                fiscalCalendarRuleSet: 0
            });
            setSelectedOrg('');
            setCreateNewOrg(false);
            fetchOrganisations();
        } catch (error) {
            const responseData = error.response?.data;
            const backendErrors = responseData?.errors;
            const formattedErrors = Array.isArray(backendErrors)
                ? backendErrors.map((item) => {
                    if (typeof item === 'string') return item;
                    if (item?.description) return item.description;
                    return null;
                }).filter(Boolean)
                : [];

            setError(
                formattedErrors.length > 0
                    ? formattedErrors.join(' ')
                    : responseData?.message || error.message || 'An error occurred'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className="component-container" sx={{ width: '100%', maxWidth: 'none', margin: '0 auto', padding: 3 }}>
            <Typography variant="h4" gutterBottom>
                Admin Dashboard
            </Typography>
            
            <Paper sx={{ padding: 3, marginTop: 3, width: { xs: '100%', md: '50%' }, mx: 'auto' }}>
                <Typography variant="h6" gutterBottom>
                    Register New User
                </Typography>
                <Box sx={{ mb: 2 }} />
                
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                
                <form onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                error={!!formErrors.email}
                                helperText={formErrors.email}
                            />
                        </Grid>
                        
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Password"
                                name="password"
                                type="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                required
                                error={!!formErrors.password}
                                helperText={formErrors.password}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth error={!!formErrors.selectedOrg}>
                                <InputLabel>Organisation</InputLabel>
                                <Select
                                    value={selectedOrg}
                                    onChange={handleOrgChange}
                                    required={!createNewOrg}
                                >
                                    <MenuItem value="">Select Organisation</MenuItem>
                                    {organisations.map((org) => (
                                        <MenuItem key={org.organisationId} value={org.organisationId}>
                                            {org.name}
                                        </MenuItem>
                                    ))}
                                    <MenuItem value="new">Create New Organisation</MenuItem>
                                </Select>
                                {formErrors.selectedOrg && (
                                    <Typography color="error" variant="caption">{formErrors.selectedOrg}</Typography>
                                )}
                            </FormControl>
                        </Grid>

                        {createNewOrg && (
                            <>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Organisation Name"
                                        name="orgName"
                                        value={formData.orgName}
                                        onChange={handleInputChange}
                                        required
                                        error={!!formErrors.orgName}
                                        helperText={formErrors.orgName}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth error={!!formErrors.startMonth}>
                                        <InputLabel>Start Month</InputLabel>
                                        <Select
                                            name="startMonth"
                                            value={formData.startMonth}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            {months.map((month) => (
                                                <MenuItem key={month.value} value={month.value}>
                                                    {month.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.startMonth && (
                                            <Typography color="error" variant="caption">{formErrors.startMonth}</Typography>
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth error={!!formErrors.startDayOfWeek}>
                                        <InputLabel>Start Day of Week</InputLabel>
                                        <Select
                                            name="startDayOfWeek"
                                            value={formData.startDayOfWeek}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            {daysOfWeek.map((day) => (
                                                <MenuItem key={day.value} value={day.value}>
                                                    {day.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.startDayOfWeek && (
                                            <Typography color="error" variant="caption">{formErrors.startDayOfWeek}</Typography>
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControl fullWidth error={!!formErrors.fiscalCalendarRuleSet}>
                                        <InputLabel>Fiscal Calendar</InputLabel>
                                        <Select
                                            name="fiscalCalendarRuleSet"
                                            value={formData.fiscalCalendarRuleSet}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            {calendarRuleSets.map((ruleSet) => (
                                                <MenuItem key={ruleSet.value} value={ruleSet.value}>
                                                    {ruleSet.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.fiscalCalendarRuleSet && (
                                            <Typography color="error" variant="caption">{formErrors.fiscalCalendarRuleSet}</Typography>
                                        )}
                                    </FormControl>
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={loading}
                                fullWidth
                            >
                                {loading ? <CircularProgress size={24} /> : 'Register User'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>

            <UserBlockManagement />
        </Box>
    );
};

export default AdminPage;
